import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'

interface EmailData {
  summary: {
    total: number
    active: number
    completed: number
    paused: number
    avgDay: number
  }
  dayDistribution: { current_day: number; count: number }[]
}

export function EmailSeriesPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-analytics-email'],
    queryFn: () => api.get<EmailData>('/api/admin/analytics/email'),
  })

  if (isLoading) {
    return <div className="flex items-center justify-center py-24"><LoadingSpinner size="lg" /></div>
  }

  const s = data?.summary
  const dist = data?.dayDistribution ?? []
  const maxCount = Math.max(...dist.map((d) => d.count), 1)

  return (
    <div>
      <h1 className="text-2xl font-heading font-semibold mb-6">Email Series</h1>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <KpiCard label="Total Enrolled" value={s?.total?.toLocaleString() ?? '0'} />
        <KpiCard label="Active" value={s?.active?.toLocaleString() ?? '0'} />
        <KpiCard label="Completed" value={s?.completed?.toLocaleString() ?? '0'} />
        <KpiCard label="Paused" value={s?.paused?.toLocaleString() ?? '0'} />
        <KpiCard label="Avg Day" value={s?.avgDay ? s.avgDay.toFixed(1) : '--'} />
      </div>

      {/* Day distribution */}
      {dist.length > 0 && (
        <Card>
          <CardHeader>
            <h3 className="font-heading font-semibold text-sm">Active Members by Day</h3>
          </CardHeader>
          <CardContent>
            <div className="flex gap-1 h-40">
              {dist.map((d) => {
                const pct = (d.count / maxCount) * 100
                return (
                  <div key={d.current_day} className="flex-1 flex flex-col items-center justify-end">
                    <div
                      className="w-full bg-[#65A0A8]/60 hover:bg-[#65A0A8] rounded-t transition-colors"
                      style={{ height: `${Math.max(4, pct)}%` }}
                      title={`Day ${d.current_day}: ${d.count} members`}
                    />
                    <span className="text-[9px] text-[var(--content-tertiary)] mt-1 tabular-nums shrink-0">{d.current_day}</span>
                  </div>
                )
              })}
            </div>
            <p className="text-xs text-[var(--content-tertiary)] text-center mt-2">Day in series</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="py-3">
        <p className="text-xs text-[var(--content-tertiary)] uppercase tracking-wider mb-1">{label}</p>
        <p className="text-xl font-heading font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  )
}
